/**
 * @file ErrorStateComponent.tsx
 * @description
 * -----------------------------------------------------
 * Vista de error
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-09
 * @modified 2025-07-23
 * @author DLTCode
 * @author Sergio Manjón
 * @author Carlos Domenech
 * @author Ismael Benabdellah
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import ImageComponent from "@/components/ui/ImageComponent";
import { HTTPStatusValue } from "@/constants/HttpStatus";
import useErrorConfig from "@/hooks/useErrorConfig";
import { HTMLProps } from "react";
import clsx from "clsx";
const IMAGE_SIZES = {
  small: {
    width: 150,
    height: 67
  },
  medium: {
    width: 225,
    height: 100
  },
  large: {
    width: 300,
    height: 133
  }
} as const;

/**
 * Props para el componente ErrorView
 */
export interface ErrorViewProps extends Omit<HTMLProps<HTMLDivElement>, "size"> {
  /** Código de estado HTTP para determinar el tipo de error a mostrar */
  status?: HTTPStatusValue;
  /** Mensaje personalizado que sobreescribe el mensaje por defecto */
  message?: string;
  /** Ruta de imagen personalizada que sobreescribe la imagen por defecto */
  customImageSrc?: string;
  /** Texto alternativo personalizado para la imagen */
  imageAlt?: string;
  /**
   * Tamaño de la vista de error (afecta las dimensiones de la imagen)
   * @default "large"
   * */
  size?: "small" | "medium" | "large";
}
export default function ErrorView(t0) {
  const $ = _c(40);
  let className;
  let customImageSrc;
  let imageAlt;
  let messageFromProps;
  let props;
  let status;
  let t1;
  if ($[0] !== t0) {
    ({
      status,
      message: messageFromProps,
      customImageSrc,
      imageAlt,
      size: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = customImageSrc;
    $[3] = imageAlt;
    $[4] = messageFromProps;
    $[5] = props;
    $[6] = status;
    $[7] = t1;
  } else {
    className = $[1];
    customImageSrc = $[2];
    imageAlt = $[3];
    messageFromProps = $[4];
    props = $[5];
    status = $[6];
    t1 = $[7];
  }
  const size = t1 === undefined ? "large" : t1;
  const t = useTranslations("Errors.ErrorPage");
  const config = useErrorConfig(status);
  const {
    imageSrc: defaultImage,
    titleKey,
    messageKey,
    altKey,
    cssModifier
  } = config;
  const src = customImageSrc ?? defaultImage;
  let t2;
  if ($[8] !== altKey || $[9] !== imageAlt || $[10] !== t) {
    t2 = imageAlt ?? t(altKey);
    $[8] = altKey;
    $[9] = imageAlt;
    $[10] = t;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const alt = t2;
  let t3;
  if ($[12] !== t || $[13] !== titleKey) {
    t3 = t(titleKey);
    $[12] = t;
    $[13] = titleKey;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const title = t3;
  let t4;
  if ($[15] !== messageFromProps || $[16] !== messageKey || $[17] !== t) {
    t4 = messageFromProps ?? t(messageKey);
    $[15] = messageFromProps;
    $[16] = messageKey;
    $[17] = t;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const message = t4;
  const imageSize = IMAGE_SIZES[size];
  const t5 = `error-view  error-view__${size}`;
  let t6;
  if ($[19] !== className || $[20] !== t5) {
    t6 = clsx(t5, className);
    $[19] = className;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== alt || $[23] !== imageSize.height || $[24] !== imageSize.width || $[25] !== src) {
    t7 = <section className="error-view__image"><ImageComponent src={src} alt={alt} width={imageSize.width} height={imageSize.height} zoomable={false} /></section>;
    $[22] = alt;
    $[23] = imageSize.height;
    $[24] = imageSize.width;
    $[25] = src;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = `error-view__content--${cssModifier}`;
  let t9;
  if ($[27] !== title) {
    t9 = <h1 className="error-view__title">{title}</h1>;
    $[27] = title;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== message) {
    t10 = <p className="error-view__message">{message}</p>;
    $[29] = message;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t8 || $[33] !== t9) {
    t11 = <section className={t8}>{t9}{t10}</section>;
    $[31] = t10;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== props || $[36] !== t11 || $[37] !== t6 || $[38] !== t7) {
    t12 = <section className={t6} {...props}>{t7}{t11}</section>;
    $[35] = props;
    $[36] = t11;
    $[37] = t6;
    $[38] = t7;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}